import { useObjectMemo } from "../../../../hooks";
import { useIsBusinessUser } from "../../../entities/users/hooks";
import { useFeature } from "../../features";
import { PERMISSION, useBusinessPermissions } from "../../permissions";

const WIRE_PERMISSIONS = [
  PERMISSION.BUSINESS.WRITE.DOMESTIC_WIRE_TRANSACTION,
  PERMISSION.BUSINESS.WRITE.SWIFT_WIRE_TEMPLATE,
  PERMISSION.BUSINESS.WRITE.SWIFT_WIRE_VIA_TEMPLATE,
  PERMISSION.BUSINESS.WRITE.SWIFT_WIRE_TRANSACTION,
  PERMISSION.BUSINESS.WRITE.FEDWIRE_TEMPLATE,
  PERMISSION.BUSINESS.WRITE.FEDWIRE_VIA_TEMPLATE,
];

export const useWirePermissions = () => {
  const { featureEnabled, featureEquals } = useFeature();
  const isBusinessUser = useIsBusinessUser();

  const businessInternationalWires =
    isBusinessUser && featureEnabled("international_wires");
  const consumerInternationalWires =
    !isBusinessUser && featureEnabled("international_wires_consumer");
  const internationalWires =
    businessInternationalWires || consumerInternationalWires;

  const showWireTransferOptionInMenu =
    featureEnabled({
      or: ["wires", "international_wires", "international_wires_consumer"],
    }) &&
    (!isBusinessUser ||
      featureEnabled({
        or: [
          "indigo.WireTransaction_permission",
          "indigo.FedwireViaTemplate_permission",
          "indigo.SwiftWireTransaction_permission",
          "indigo.SwiftWireViaTemplate_permission",
        ],
      }));
  const fedwireTemplatesEnabled = featureEquals("wire_templates", "fedwire");
  const internationalWireTemplatesEnabled =
    businessInternationalWires &&
    featureEnabled("international_wire_templates");

  const [
    hasFedWireFreeformPermission,
    hasManageIntlWireTemplatesPermission,
    hasSendIntlWireViaTemplatesPermission,
    hasSendIntlWireFreeformPermission,
    hasFedwireTemplatePermission,
    hasFedwireViaTemplatePermission,
  ] = useBusinessPermissions(WIRE_PERMISSIONS);

  const showManageTemplatesUI =
    fedwireTemplatesEnabled && hasFedwireTemplatePermission;

  const showManageIntlWireTemplatesUI =
    internationalWireTemplatesEnabled && hasManageIntlWireTemplatesPermission;

  const canSendBusinessInternationalWire =
    businessInternationalWires &&
    (hasSendIntlWireFreeformPermission ||
      (internationalWireTemplatesEnabled &&
        hasSendIntlWireViaTemplatesPermission));
  const canSendConsumerInternationalWire = consumerInternationalWires;
  const canSendInternationalWire =
    canSendBusinessInternationalWire || canSendConsumerInternationalWire;
  const canSendDomesticWire =
    featureEnabled("wires") &&
    (hasFedWireFreeformPermission || hasFedwireViaTemplatePermission);

  return useObjectMemo({
    showWireTransferOptionInMenu,
    fedwireTemplatesEnabled,
    showManageTemplatesUI,
    internationalWires,
    businessInternationalWires,
    consumerInternationalWires,
    internationalWireTemplatesEnabled,
    showManageIntlWireTemplatesUI,
    canSendInternationalWire,
    canSendBusinessInternationalWire,
    canSendConsumerInternationalWire,
    canSendDomesticWire,
    hasManageIntlWireTemplatesPermission,
    hasSendIntlWireViaTemplatesPermission,
    hasSendIntlWireFreeformPermission,
    hasFedWireFreeformPermission,
    hasFedwireTemplatePermission,
    hasFedwireViaTemplatePermission,
  });
};
